// Direct selection (A) and Lasso (Q): select anchor points (Shift adds / removes), drag them, drag their
// handles (a smooth anchor keeps its handles in line; Alt breaks them), click a segment to select its two
// anchors and drag the segment, drag on empty canvas for a marquee (A) or a freehand lasso (Q) of anchors.
// Editing a live shape's anchors turns it into a path. Delete removes the selected anchors (keyboard).
import type { Tool, ToolHost, ToolPointer } from './tool';
import { useDesign } from '../state';
import { useDesignUi } from '../ui/designUi';
import { hitAnchor, hitHandle, hitSegment, localDelta, worldSubpaths, type AnchorRef } from './pathTarget';
import { geometry, type DisplayList } from '../render/displayList';
import { setSubpaths } from '../commands/commands';
import { moveAnchor, moveHandle } from '../geometry/pathOps';
import { toScreen } from '../render/viewMath';
import { drawSnapGuides, snapContext, type SnapCtx } from './snapContext';
import type { Guide } from '../geometry/snapping';
import type { Subpath } from '../model/types';
import { cssVar } from '../../app/theme';

const TOL = 5; // screen px

type Drag =
  | { kind: 'none' }
  | { kind: 'anchors'; x0: number; y0: number; start: Map<string, Subpath[]>; snap: SnapCtx; lead: [number, number] | null }
  | { kind: 'handle'; ref: AnchorRef; which: 'in' | 'out'; start: Subpath[] }
  | { kind: 'marquee'; pts: [number, number][]; add: boolean };

let gesture = 0;
const key = (a: AnchorRef) => `${a.id}/${a.sp}/${a.i}`;

export class DirectTool implements Tool {
  private drag: Drag = { kind: 'none' };
  private merge = '';
  private guides: Guide[] = [];
  constructor(readonly lasso = false) {}

  down(p: ToolPointer, h: ToolHost) {
    this.merge = `direct-${++gesture}`;
    const ui = useDesignUi.getState();
    const tol = TOL * p.px;
    const hd = hitHandle(h.list, ui.anchors, p.wx, p.wy, tol);
    if (hd) {
      this.drag = {
        kind: 'handle',
        ref: hd,
        which: hd.which,
        start: structuredClone(geometry(hd.item.node).subpaths),
      };
      return;
    }
    const an = this.lasso ? null : hitAnchor(h.list, p.wx, p.wy, tol);
    let sel = ui.anchors;
    if (an) {
      const ref = { id: an.id, sp: an.sp, i: an.i };
      const has = sel.some((a) => key(a) === key(ref));
      if (p.shift) sel = has ? sel.filter((a) => key(a) !== key(ref)) : [...sel, ref];
      else if (!has) sel = [ref];
    } else {
      const seg = this.lasso ? null : hitSegment(h.list, p.wx, p.wy, tol);
      if (seg) {
        const n = seg.item.node;
        const count = geometry(n).subpaths[seg.sp]!.points.length;
        const refs = [
          { id: seg.item.id, sp: seg.sp, i: seg.seg },
          { id: seg.item.id, sp: seg.sp, i: (seg.seg + 1) % count },
        ];
        sel = p.shift ? [...sel, ...refs.filter((r) => !sel.some((a) => key(a) === key(r)))] : refs;
      } else {
        if (!p.shift) sel = [];
        ui.set({ anchors: sel });
        this.drag = { kind: 'marquee', pts: [[p.wx, p.wy]], add: p.shift };
        return;
      }
    }
    ui.set({
      anchors: sel,
      selection: [...new Set(sel.map((a) => h.list.byId.get(a.id)?.topId).filter((x): x is string => !!x))],
    });
    const start = new Map<string, Subpath[]>();
    for (const a of sel) {
      const it = h.list.byId.get(a.id);
      if (it && !start.has(a.id)) start.set(a.id, structuredClone(geometry(it.node).subpaths));
    }
    // The anchor under the pointer (or the first selected) leads: it snaps, the others follow.
    const leadRef = an ? { id: an.id, sp: an.sp, i: an.i } : sel[0];
    const leadIt = leadRef && h.list.byId.get(leadRef.id);
    const lp = leadIt && worldSubpaths(leadIt)[leadRef!.sp]?.points[leadRef!.i];
    this.drag = { kind: 'anchors', x0: p.wx, y0: p.wy, start, snap: snapContext([], [...start.keys()]), lead: lp ? [lp.x, lp.y] : null };
  }

  move(p: ToolPointer, h: ToolHost, dragging: boolean) {
    const d = this.drag;
    if (!dragging || d.kind === 'none') {
      const tol = TOL * p.px;
      const over =
        hitHandle(h.list, useDesignUi.getState().anchors, p.wx, p.wy, tol) ||
        (!this.lasso && hitAnchor(h.list, p.wx, p.wy, tol));
      h.setCursor(over ? 'pointer' : this.lasso ? 'crosshair' : 'default');
      return;
    }
    const st = useDesign.getState();
    if (d.kind === 'marquee') {
      if (this.lasso) d.pts.push([p.wx, p.wy]);
      else d.pts = [d.pts[0]!, [p.wx, p.wy]];
      h.redrawOverlay();
      return;
    }
    if (d.kind === 'anchors') {
      let dx = p.wx - d.x0,
        dy = p.wy - d.y0;
      if (p.shift) {
        if (Math.abs(dx) > Math.abs(dy)) dy = 0;
        else dx = 0;
        this.guides = [];
      } else if (d.lead) {
        const s = d.snap.point(d.lead[0] + dx, d.lead[1] + dy);
        dx = s.x - d.lead[0];
        dy = s.y - d.lead[1];
        this.guides = s.guides;
      }
      const sel = useDesignUi.getState().anchors;
      for (const [id, start] of d.start) {
        const it = h.list.byId.get(id);
        if (!it) continue;
        const [lx, ly] = localDelta(it.world, dx, dy);
        const next = start.map((sp, s) => ({
          closed: sp.closed,
          points: sp.points.map((pt, i) =>
            sel.some((a) => a.id === id && a.sp === s && a.i === i) ? moveAnchor(pt, lx, ly) : pt,
          ),
        }));
        st.run(setSubpaths(id, next, this.merge));
      }
      return;
    }
    if (d.kind === 'handle') {
      const it = h.list.byId.get(d.ref.id);
      if (!it) return;
      // World pointer → the node's local coordinates.
      const m = it.world;
      const det = m[0] * m[3] - m[1] * m[2] || 1e-12;
      const x = p.wx - m[4],
        y = p.wy - m[5];
      const lx = (m[3] * x - m[2] * y) / det,
        ly = (-m[1] * x + m[0] * y) / det;
      const next = d.start.map((sp, s) =>
        s !== d.ref.sp
          ? sp
          : {
              closed: sp.closed,
              points: sp.points.map((pt, i) => (i === d.ref.i ? moveHandle(pt, d.which, lx, ly, p.alt) : pt)),
            },
      );
      st.run(setSubpaths(d.ref.id, next, this.merge));
    }
  }

  up(_p: ToolPointer, h: ToolHost) {
    const d = this.drag;
    if (d.kind === 'marquee' && d.pts.length > 1) {
      const inside = this.lasso ? lassoTest(d.pts) : rectTest(d.pts[0]!, d.pts[1]!);
      const found: AnchorRef[] = [];
      for (const it of h.list.items) {
        if (it.hidden || it.locked || (it.node.kind !== 'path' && it.node.kind !== 'shape')) continue;
        worldSubpaths(it).forEach((sp, s) =>
          sp.points.forEach((pt, i) => inside(pt.x, pt.y) && found.push({ id: it.id, sp: s, i })),
        );
      }
      const ui = useDesignUi.getState();
      const anchors = d.add
        ? [...ui.anchors, ...found.filter((f) => !ui.anchors.some((a) => key(a) === key(f)))]
        : found;
      ui.set({
        anchors,
        selection: [
          ...new Set(anchors.map((a) => h.list.byId.get(a.id)?.topId).filter((x): x is string => !!x)),
        ],
      });
    }
    this.drag = { kind: 'none' };
    this.guides = [];
    useDesign.getState().endGesture();
    h.redrawOverlay();
  }

  overlay(g: CanvasRenderingContext2D, v: ReturnType<ToolHost['view']>, h: ToolHost) {
    const d = this.drag;
    drawSnapGuides(g, v, this.guides);
    if (d.kind === 'marquee' && d.pts.length > 1) {
      g.strokeStyle = cssVar('--sel');
      g.setLineDash([3, 3]);
      g.beginPath();
      if (this.lasso)
        d.pts.forEach(([x, y], i) => (i ? g.lineTo(...toScreen(v, x, y)) : g.moveTo(...toScreen(v, x, y))));
      else {
        const [x0, y0] = toScreen(v, ...d.pts[0]!),
          [x1, y1] = toScreen(v, ...d.pts[1]!);
        g.rect(Math.min(x0, x1) + 0.5, Math.min(y0, y1) + 0.5, Math.abs(x1 - x0), Math.abs(y1 - y0));
      }
      g.stroke();
      g.setLineDash([]);
    }
    drawAnchors(g, v, h.list, useDesignUi.getState().anchors);
  }
}

const rectTest = (a: [number, number], b: [number, number]) => (x: number, y: number) =>
  x >= Math.min(a[0], b[0]) &&
  x <= Math.max(a[0], b[0]) &&
  y >= Math.min(a[1], b[1]) &&
  y <= Math.max(a[1], b[1]);
const lassoTest = (poly: [number, number][]) => (x: number, y: number) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = poly[i]!,
      [xj, yj] = poly[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

/** Outlines of the paths that have selected anchors, every anchor of them (filled when selected), and the
 *  handles of the selected anchors. Shared by the path tools. */
export function drawAnchors(
  g: CanvasRenderingContext2D,
  v: ReturnType<ToolHost['view']>,
  list: DisplayList,
  anchors: AnchorRef[],
  extraIds: string[] = [],
) {
  const ids = new Set([...anchors.map((a) => a.id), ...extraIds]);
  const sel = new Set(anchors.map(key));
  g.lineWidth = 1;
  for (const id of ids) {
    const it = list.byId.get(id);
    if (!it) continue;
    const sps = worldSubpaths(it);
    g.strokeStyle = cssVar('--sel');
    g.beginPath();
    for (const sp of sps) {
      const pts = sp.points;
      if (!pts.length) continue;
      g.moveTo(...toScreen(v, pts[0]!.x, pts[0]!.y));
      const n = sp.closed ? pts.length : pts.length - 1;
      for (let i = 0; i < n; i++) {
        const a = pts[i]!,
          b = pts[(i + 1) % pts.length]!;
        g.bezierCurveTo(
          ...toScreen(v, a.outX, a.outY),
          ...toScreen(v, b.inX, b.inY),
          ...toScreen(v, b.x, b.y),
        );
      }
      if (sp.closed) g.closePath();
    }
    g.stroke();
    sps.forEach((sp, s) =>
      sp.points.forEach((pt, i) => {
        const [x, y] = toScreen(v, pt.x, pt.y);
        const on = sel.has(`${id}/${s}/${i}`);
        if (on)
          for (const [hx, hy] of [
            [pt.inX, pt.inY],
            [pt.outX, pt.outY],
          ] as const) {
            if (hx === pt.x && hy === pt.y) continue;
            const [sx, sy] = toScreen(v, hx, hy);
            g.beginPath();
            g.moveTo(x, y);
            g.lineTo(sx, sy);
            g.stroke();
            g.beginPath();
            g.arc(sx, sy, 3, 0, Math.PI * 2);
            g.fillStyle = cssVar('--sel');
            g.fill();
          }
        g.fillStyle = on ? cssVar('--sel') : cssVar('--pure-white');
        g.fillRect(Math.round(x) - 3, Math.round(y) - 3, 6, 6);
        g.strokeRect(Math.round(x) - 2.5, Math.round(y) - 2.5, 5, 5);
      }),
    );
  }
}
